import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { uniqBy } from "lodash";
import { useCallback, useMemo } from "react";
import * as React from "react";
import { Editor } from "slate";
import { useSlate } from "slate-react";

import { ProgressBar, UploadBar } from "@livechat/design-system";
import { DesignToken } from "@livechat/design-system-react-components";

import { Icon, IconsEnum } from "components/icon";
import { extractResourceIDsFromRichTextObj } from "helpers/text-editor";
import { ITransactionAttachment } from "interfaces/entities/transaction";
import { getItem } from "services/session-storage";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { useAttachments } from "./AttachmentProvider";

export interface IUploadingAttachment {
  localID: string;
  file: File;
  progress?: number;
}

const AttachmentEntryContainer = styled.div<{ privateMode: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  padding: 10px 12px;
  padding-right: 0;
  padding-bottom: 0;
  font-size: 14px;
  color: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => withVar(props.privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)
  };
`;

interface ITransactionAttachmentEntryProps {
  name: string;
  id: string;
  privateMode: boolean;
  onRemove: (attachmentID: string) => void;
}

const removeIconCss = css`
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
`;

const RemoveControl = styled.span`
  padding: 7px;
  cursor: pointer;
  margin-left: auto;
`;

const AttachmentHeader = styled.span`
  flex: 1;
  padding-bottom: 4px;
`;

const UploadBarContainer = styled.div<{ privateMode: boolean }>`
  .lc-upload-bar {
    background-color: ${(props): string =>
      withVar(props.privateMode ? DesignToken.SurfaceInvertPrimary : DesignToken.SurfacePrimaryDefault)};
    border: none;
    margin-bottom: 8px;
  }

  .lc-upload-bar__list {
    padding-right: 0;
  }

  .lc-upload-bar__title {
    color: ${(props): string =>
      withVar(props.privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)};
  }

  .lc-upload-bar__collapse-btn {
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }

  .lc-collapse-icon {
    fill: ${(props): string =>
      withVar(props.privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)};

    &:hover {
      opacity: 1;
    }
  }
`;

const TransactionAttachmentEntry: React.FC<ITransactionAttachmentEntryProps> = ({
  id,
  name,
  onRemove,
  privateMode,
}) => {
  const handleAttachmentRemove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      event.preventDefault();
      onRemove(id);
    },
    [onRemove, id]
  );

  return (
    <AttachmentEntryContainer privateMode={privateMode}>
      <AttachmentHeader>{name}</AttachmentHeader>
      <RemoveControl onMouseDown={handleAttachmentRemove}>
        <Icon
          name={IconsEnum.Close}
          fill={withVar(privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)}
          css={removeIconCss}
          width="12px"
          height="12px"
        />
      </RemoveControl>
    </AttachmentEntryContainer>
  );
};

const TransactionAttachmentsList: React.FC<{ privateMode: boolean; visibleAttachments: ITransactionAttachment[] }> = ({
  privateMode,
  visibleAttachments,
}) => {
  const { deleteAttachment } = useAttachments();
  const removeAttachment = useCallback(
    (attachmentID: string) => {
      deleteAttachment(attachmentID);
    },
    [deleteAttachment]
  );

  return (
    <>
      {visibleAttachments.map(attachment => (
        <TransactionAttachmentEntry
          key={attachment.id}
          id={attachment.id}
          name={attachment.name}
          onRemove={removeAttachment}
          privateMode={privateMode}
        />
      ))}
    </>
  );
};

interface IUploadingAttachmentEntryProps {
  id: string;
  onAbort: (localID: string) => void;
  name: string;
  privateMode: boolean;
  progress?: number;
}

const UploadingAttachmentEntry: React.FC<IUploadingAttachmentEntryProps> = ({
  id,
  onAbort,
  name,
  progress = 0.0,
  privateMode,
}) => {
  const handleAttachmentAbort = useCallback(
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      event.stopPropagation();
      event.preventDefault();
      onAbort(id);
    },
    [onAbort, id]
  );

  return (
    <AttachmentEntryContainer privateMode={privateMode}>
      <AttachmentHeader>
        {name}
        <ProgressBar percent={progress} />
      </AttachmentHeader>
      <RemoveControl onMouseDown={handleAttachmentAbort}>
        <Icon
          name={IconsEnum.Close}
          fill={withVar(privateMode ? DesignToken.ContentInvertPrimary : DesignToken.ContentBasicPrimary)}
          css={removeIconCss}
          width="12px"
          height="12px"
        />
      </RemoveControl>
    </AttachmentEntryContainer>
  );
};

interface IUploadingAttachmentsListProps {
  uploadingAttachments: IUploadingAttachment[];
  onAbort: (localID: string) => void;
  privateMode: boolean;
}

const UploadingAttachmentsList: React.FC<IUploadingAttachmentsListProps> = ({
  uploadingAttachments,
  onAbort,
  privateMode,
}) => (
  <>
    {uploadingAttachments.map(attachment => (
      <UploadingAttachmentEntry
        key={attachment.localID}
        id={attachment.localID}
        onAbort={onAbort}
        name={attachment.file.name}
        progress={attachment.progress}
        privateMode={privateMode}
      />
    ))}
  </>
);

interface ITicketAttachmentsProps {
  editor?: Editor;
  privateMode: boolean;
  uploadingAttachments: IUploadingAttachment[];
  onAbortUpload: (localID: string) => void;
}

const TicketAttachments: React.FC<ITicketAttachmentsProps> = ({
  uploadingAttachments,
  privateMode,
  onAbortUpload,
  editor,
}) => {
  const slateEditor = useSlate();
  const richTextEditor = editor || slateEditor;
  const { transactionID, attachments } = useAttachments();
  const visibleAttachments = useMemo(() => {
    if (transactionID) {
      const inlineImagesList = getItem(`${transactionID}_inlineImageAttachments`) || [];

      if (richTextEditor?.children) {
        const resourceIDs = extractResourceIDsFromRichTextObj(richTextEditor.children);
        const result = attachments.filter(({ cid }) => !inlineImagesList.includes(cid) || !resourceIDs.includes(cid));

        return uniqBy(result, item => item.cid);
      }

      return attachments.filter(({ cid }) => !inlineImagesList.includes(cid));
    }
    return [];
  }, [attachments, transactionID, richTextEditor?.children]);
  const attachmentsCount = uploadingAttachments.length + visibleAttachments.length;
  const totalProgress = useMemo(() => {
    const totalPercentage = uploadingAttachments.length * 100.0;
    const currentPercentage = uploadingAttachments.reduce(
      (total, uploadingAttachment) => total + (uploadingAttachment.progress || 0.0),
      0
    );

    if (uploadingAttachments.length === 0) {
      return 100.0;
    }
    return (currentPercentage / totalPercentage) * 100.0;
  }, [uploadingAttachments]);

  const title = `${attachmentsCount - uploadingAttachments.length}/${attachmentsCount} ${plural(
    attachmentsCount,
    "attachment",
    "attachments"
  )} uploaded`;

  return attachmentsCount > 0 ? (
    <UploadBarContainer privateMode={privateMode}>
      <UploadBar
        title={title}
        percent={totalProgress}
        mode="multiple"
        status={Math.abs(totalProgress - 100.0) < 0.01 ? "success" : "normal"}
      >
        <TransactionAttachmentsList privateMode={privateMode} visibleAttachments={visibleAttachments} />
        <UploadingAttachmentsList
          uploadingAttachments={uploadingAttachments}
          onAbort={onAbortUpload}
          privateMode={privateMode}
        />
      </UploadBar>
    </UploadBarContainer>
  ) : null;
};

export default TicketAttachments;
